import { FEATURE_FLAGS, TradeToEarnCampaignStatus } from "@cede/types";
import { QueryKeys, API_ENDPOINT } from "@cede/utils";
import { useRealtimeStats } from "./useRealtimeStats";
import { LeaderboardEntry } from "@/components/features/leaderboards/leaderboard/Leaderboard";
import { TradeStatItem } from "@/components/features/trade/TradeStats/types";
import { useCampaigns } from "@/hooks/campaign/useCampaigns";
import { useApi } from "@/hooks/core/useApi";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

export { useUserStats } from "./useUserStats";

export const useGeneralStats = () => {
  const apiHelper = useApi();
  const isRealtimeEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.IMPROVE_UX_HUB_REALTIME_STATS);

  // Connect to SSE when feature flag is enabled
  // SSE updates will be pushed to the React Query cache
  useRealtimeStats(isRealtimeEnabled);

  const { data: stats, isLoading } = useQuery({
    queryKey: [QueryKeys.GENERAL_STATS],
    queryFn: () => apiHelper.getOrThrow(API_ENDPOINT.HUB_STATS),
    // When SSE is enabled, reduce polling frequency as it's just a fallback
    refetchInterval: isRealtimeEnabled ? 60_000 : 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000,
  });

  // Use campaigns endpoint for live count — it respects visibility (excludes private campaigns the user can't see),
  // unlike hub/stats which counts all campaigns globally including private ones.
  const { totalCount: liveCampaignCount } = useCampaigns({
    status: [TradeToEarnCampaignStatus.LIVE],
    size: 1,
    skipUserStats: true,
  });

  if (!stats) return { stats: null, isLoading, isRealtime: isRealtimeEnabled };

  const data: Record<string, TradeStatItem> = {
    totalVolumeTraded: {
      title: "Total Volume Traded",
      value: stats.totalVolumeTraded.toNumber(),
      isMoney: true,
    },
    totalTradesRealized: {
      title: "Total Trades Completed",
      value: stats.totalNumberOfTrades,
      isMoney: false,
      isInteger: true,
    },
    totalVolumeRewarded: {
      title: "Total Rewards Distributed",
      value: stats.totalRewardsReceived.toNumber(),
      isMoney: true,
    },
    campaignsLive: {
      title: "Live Campaigns",
      value: liveCampaignCount,
      isMoney: false,
      isInteger: true,
    },
    campaignsDone: {
      title: "Completed Campaigns",
      value: stats.completedCampaigns,
      isMoney: false,
      isInteger: true,
    },
  };

  return {
    stats: data,
    isLoading: isLoading,
    /** Whether real-time updates are enabled (for animated display) */
    isRealtime: isRealtimeEnabled,
  };
};

export const useGlobalLeaderboard = () => {
  const { hubUser } = useHubUser();
  const apiHelper = useApi();

  const { data: leaderboard, isLoading } = useQuery({
    queryKey: [QueryKeys.GLOBAL_LEADERBOARD],
    queryFn: () => apiHelper.getOrThrow(API_ENDPOINT.HUB_GLOBAL_LEADERBOARD),
    refetchInterval: 30_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000,
  });

  const formattedLeaderboard: LeaderboardEntry[] | undefined = leaderboard?.map((entry) => ({
    rank: entry.rank,
    address: entry.defiAddress,
    totalVolumeTraded: entry.totalVolume.toNumber(),
    highlight: entry.defiAddress === hubUser?.defiAddress,
  }));

  return {
    leaderboard: formattedLeaderboard?.slice(0, 10) || [],
    isLoading,
  };
};
